import {
  NavigationProp,
  RouteProp,
  useNavigation,
  useRoute,
} from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { format, isValid, parse } from "date-fns";
import { Suspense, useEffect, useLayoutEffect } from "react";
import { StatusBar } from "react-native";
import { PreloadedQuery, usePreloadedQuery, useQueryLoader } from "react-relay";

import { ClubStackNavigatorParamList } from "@/navigators/ClubNavigator";
import { AgendaLayout } from "@whoosh/design-system/src/apps/PatronApp/layout/AgendaLayout";
import { Loader } from "@whoosh/design-system/src/components/display";
import { Box } from "@whoosh/design-system/src/components/primitive";

import { DepartmentAvailabilityScreenQuery } from "./__generated__/DepartmentAvailabilityScreenQuery.graphql";
import { CourseList } from "./components/CourseList";
import { DepartmentAvailabilityHeader } from "./components/DepartmentAvailabilityHeader";
import { DepartmentAvailabilitySidebar } from "./components/DepartmentAvailabilitySidebar";

export const DepartmentAvailabilityScreen = () => {
  const route =
    useRoute<
      RouteProp<ClubStackNavigatorParamList, "DepartmentAvailability">
    >();

  const departmentSlug = route.params?.departmentSlug ?? "";

  const [queryRef, loadQuery] =
    useQueryLoader<DepartmentAvailabilityScreenQuery>(
      DepartmentAvailabilityScreenQueryString
    );

  useEffect(() => {
    if (departmentSlug) {
      loadQuery({ departmentSlug }, { fetchPolicy: "store-and-network" });
    }
  }, [departmentSlug, loadQuery]);

  return (
    <Box flex={1} safeAreaTop backgroundColor="surface-primary">
      <StatusBar
        animated={true}
        barStyle="dark-content"
        backgroundColor="white"
      />
      <WhiteBoxToCoverHeaderShadow />
      <Suspense fallback={<Loader />}>
        {queryRef ? (
          <DepartmentAvailabilityScreenInner queryRef={queryRef} />
        ) : null}
      </Suspense>
    </Box>
  );
};

type InnerProps = {
  queryRef: PreloadedQuery<DepartmentAvailabilityScreenQuery>;
};

const DepartmentAvailabilityScreenInner = ({ queryRef }: InnerProps) => {
  const route =
    useRoute<
      RouteProp<ClubStackNavigatorParamList, "DepartmentAvailability">
    >();

  const navigation =
    useNavigation<
      NavigationProp<ClubStackNavigatorParamList, "DepartmentAvailability">
    >();

  const date = route.params?.date ?? "";

  const isDateValid = isValid(parse(date, "yyyy-MM-dd", new Date()));

  useLayoutEffect(() => {
    if (!isDateValid || !date || date === "today") {
      const today = format(new Date(), "yyyy-MM-dd");
      navigation.setParams({
        date: today,
      });
    }
  }, [date, isDateValid, navigation]);

  const data = usePreloadedQuery(
    DepartmentAvailabilityScreenQueryString,
    queryRef
  );

  const department = data.department;
  const session = data.session;

  return isDateValid ? (
    <AgendaLayout
      shouldCenter
      agenda={<CourseList departmentRef={department ?? null} />}
      header={
        <DepartmentAvailabilityHeader
          departmentRef={department ?? null}
          sessionRef={session ?? null}
        />
      }
      sidebarContent={
        <DepartmentAvailabilitySidebar
          departmentRef={department ?? null}
          sessionRef={session ?? null}
        />
      }
    />
  ) : null;
};

const DepartmentAvailabilityScreenQueryString = graphql`
  query DepartmentAvailabilityScreenQuery($departmentSlug: String!) {
    session {
      ...DepartmentAvailabilityHeaderFragment_session
      ...DepartmentAvailabilitySidebar_session
    }
    department(slug: $departmentSlug) {
      id
      ...CourseList_department
      ...DepartmentAvailabilityHeaderFragment_department
      ...DepartmentAvailabilitySidebarFragment_department
    }
  }
`;

// This is used to cover up the shadow of the header that appears in the status bar.
const WhiteBoxToCoverHeaderShadow = () => {
  return (
    <Box
      height={60}
      style={{ marginTop: -60, zIndex: 1 }}
      bg="surface-primary"
    />
  );
};
